import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation } from '@angular/core';
import { RouterModule } from '@angular/router';
import { SpSharedModule } from '../shared/sp-shared.module';
import { WorkflowSidebarComponent } from './components/workflow-sidebar.component';
import { SpResizableDirective } from '../shared/sp-resizable.directive';


/**
 * Unified /workflow shell — Round 6 (May 2026).
 *
 * <p>Replaces the Round 5-and-earlier monolithic SkyPlusWorkflowComponent
 * that had hardcoded knowledge of contract-tasks and mail-tasks view
 * modes and did sidebar item dispatching internally via a {@code viewMode}
 * flag. That logic now lives in route-bound child components — Contract,
 * Mail, and kernel workflows each have their own routes and pages.
 *
 * <h2>Layout</h2>
 *
 * <pre>
 *  ┌─ sp-header ────────────────────────────────────────────────┐
 *  │                                                             │
 *  ├─ aside.sp-workflow-sidebar   │   main.sp-workflow-content   │
 *  │   &lt;app-workflow-sidebar&gt;     │   &lt;router-outlet&gt;           │
 *  │   (legacy categories +       │   • empty landing            │
 *  │    kernel tree, unified)     │   • contract/:stageId        │
 *  │                              │   • mail/:stageId            │
 *  │                              │   • r/:wfKey/:taskKey ...    │
 *  └─────────────────────────────┴─────────────────────────────┘
 * </pre>
 *
 * <h2>What the shell owns</h2>
 *
 * <p>Almost nothing dynamic. The shell is layout chrome. Sidebar data
 * subscriptions (MenuService for legacy, WfRuntimeMenuApi for kernel)
 * live in {@link WorkflowSidebarComponent}. Mail badge counts live in
 * the sidebar too (it's the only thing that cares about them). Route
 * state lives in the URL and is read by the route-bound children.
 *
 * <p>The one piece of state the shell DOES own is {@link navCollapsed} —
 * a UI preference that affects layout chrome (the sidebar width), so it
 * belongs at the layout level rather than inside the sidebar component
 * itself. State persists per user in {@code localStorage} under
 * {@code sp-workflow-nav-collapsed}.
 *
 * <h2>Selector preservation</h2>
 *
 * <p>The selector {@code app-skyplus-workflow} is kept verbatim from
 * pre-Round-6 so any HTML that embeds this component (rare — usually
 * routed only) continues working without changes. The class name is
 * also preserved so {@code workflow.routes.ts}, app.routes.ts imports,
 * and any direct references resolve correctly.
 *
 * <h2>Why ViewEncapsulation.None</h2>
 *
 * <p>The pre-Round-6 sidebar HTML lived directly in this component's
 * template, so its 2,000+ lines of {@code .sp-category-*} / {@code .sp-stage-*}
 * styles in {@code skyplus-workflow.component.scss} were emulation-scoped
 * to the same component and worked transparently. Round 6 extracts the
 * sidebar into {@link WorkflowSidebarComponent}, which lives in a separate
 * component and therefore wouldn't pick up those scoped styles. Setting
 * {@code ViewEncapsulation.None} on the shell makes that stylesheet global
 * so the extracted sidebar — and any future child route component — can
 * reuse the same class hooks without per-component duplication. Same
 * pattern used by {@code WfRuntimeComponent}.
 */
@Component({
  selector: 'app-skyplus-workflow',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, RouterModule, SpSharedModule, WorkflowSidebarComponent, SpResizableDirective],
  templateUrl: './skyplus-workflow.component.html',
  styleUrls: ['./skyplus-workflow.component.scss']
})
export class SkyPlusWorkflowComponent {

  /**
   * Whether the left navigation is collapsed to its toggle-only strip.
   * Hydrated from {@code localStorage} on construction so the initial
   * paint is in the right state — no flash from expanded to collapsed.
   *
   * <p>When true, CSS overrides the {@code spResizable} directive's
   * inline width with {@code 44px !important} so only the toggle
   * button is visible. When false, the directive's stored width
   * applies as usual.
   */
  navCollapsed: boolean = this.readCollapsedFromStorage();

  /** Flip the collapsed state and persist the new value. */
  toggleNav(): void {
    this.navCollapsed = !this.navCollapsed;
    try {
      localStorage.setItem('sp-workflow-nav-collapsed', this.navCollapsed ? '1' : '0');
    } catch {
      /* localStorage can throw in incognito / privacy modes — swallow */
    }
  }

  private readCollapsedFromStorage(): boolean {
    try {
      return localStorage.getItem('sp-workflow-nav-collapsed') === '1';
    } catch {
      return false;
    }
  }
}